import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, EmptyState } from '@/backoffice/components/ui';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type Notification,
} from '@/domains/client/services/client.service';
import { formatRelativeTime } from '@/backoffice/utils';
import { Bell, CheckCheck, Loader2, Package, CreditCard, Truck } from 'lucide-react';
import { cn } from '@/utils/cn';

function notificationIcon(type?: string) {
  switch (type) {
    case 'payment':
      return <CreditCard className="w-5 h-5 text-amber-600" />;
    case 'delivery':
      return <Truck className="w-5 h-5 text-blue-600" />;
    default:
      return <Package className="w-5 h-5 text-teal-600" />;
  }
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const load = () => {
    setLoading(true);
    fetchNotifications()
      .then(setNotifications)
      .catch(() => setError('Impossible de charger les notifications.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const unreadCount = notifications.filter((n) => !n.est_lu).length;

  const handleMarkRead = async (notification: Notification) => {
    if (notification.est_lu) return;
    try {
      await markNotificationAsRead(notification.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, est_lu: true } : n)),
      );
    } catch {
      setError('Impossible de marquer la notification comme lue.');
    }
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, est_lu: true })));
    } catch {
      setError('Impossible de marquer toutes les notifications comme lues.');
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <DashboardLayout
      userType="client"
      title="Notifications"
      breadcrumbs={[
        { label: 'Tableau de bord', path: '/dashboard/client' },
        { label: 'Notifications' },
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-teal-600" />
            <p className="text-sm text-gray-600">
              {unreadCount > 0
                ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
                : 'Toutes les notifications sont lues'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAll} disabled={markingAll}>
              {markingAll ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCheck className="w-4 h-4 mr-2" />
              )}
              Tout marquer comme lu
            </Button>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            icon={<Bell className="w-12 h-12 text-gray-300" />}
            title="Aucune notification"
            description="Vos alertes de commande et de paiement apparaîtront ici."
          />
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card key={notification.id}>
                <button
                  type="button"
                  onClick={() => handleMarkRead(notification)}
                  className={cn(
                    'w-full text-left p-4 flex gap-4 transition-colors',
                    !notification.est_lu && 'bg-teal-50/50',
                  )}
                >
                  <div className="shrink-0 mt-0.5">
                    {notificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-gray-900">{notification.titre}</p>
                      {!notification.est_lu && (
                        <Badge variant="info">Nouveau</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {formatRelativeTime(notification.created_at)}
                    </p>
                    {notification.reference_id && notification.type === 'payment' && (
                      <Link
                        to={`/dashboard/client/commandes/${notification.reference_id}/paiement`}
                        className="inline-block text-sm text-teal-600 hover:text-teal-700 mt-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Voir la commande →
                      </Link>
                    )}
                    {notification.reference_id && notification.type !== 'payment' && (
                      <Link
                        to={`/dashboard/client/commandes/${notification.reference_id}`}
                        className="inline-block text-sm text-teal-600 hover:text-teal-700 mt-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Voir la commande →
                      </Link>
                    )}
                  </div>
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
